// src/employee/pages/EmployeeProfile.js
import React, { useEffect, useState } from "react";
import { doc, getDoc, updateDoc, setDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { db, storage } from "../../firebase";
import { useEmployeeAuth } from "../auth/EmployeeAuthContext";
import { useEmployeeData } from "../context/EmployeeDataContext";
import { motion, AnimatePresence } from "framer-motion";
import { FaUserEdit, FaCamera, FaEnvelope, FaEdit, FaKey, FaCheck, FaTimes, FaSignOutAlt } from "react-icons/fa";

export default function EmployeeProfile() {
  const { user, logout } = useEmployeeAuth();
  const { employee } = useEmployeeData();
  const [uploading, setUploading] = useState(false);
  const [showBankForm, setShowBankForm] = useState(false);
  const [showPassForm, setShowPassForm] = useState(false);
  const [bank, setBank] = useState({ name: "", bankName: "", account: "", ifsc: "" });
  const [savingBank, setSavingBank] = useState(false);
  const [bankSuccess, setBankSuccess] = useState(false);
  const [currentPass, setCurrentPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [changing, setChanging] = useState(false);
  const [passError, setPassError] = useState("");
  const [passSuccess, setPassSuccess] = useState("");

  // Load bank details
  useEffect(() => {
    const loadBank = async () => {
      const bankRef = doc(db, "employees", user.email, "bankDetails", "info");
      const snap = await getDoc(bankRef);
      if (snap.exists()) {
        setBank(snap.data());
        setShowBankForm(false);
      } else {
        setShowBankForm(true); // Show form if not exists
      }
    };
    loadBank();
  }, [user.email]);

  const handleImageUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const storageRef = ref(storage, `profilePics/${user.email}`);
      const uploadTask = uploadBytesResumable(storageRef, file);
      uploadTask.on("state_changed", null, console.error, async () => {
        const url = await getDownloadURL(uploadTask.snapshot.ref);
        await updateDoc(doc(db, "employees", user.email), { profilePic: url });
        setUploading(false);
      });
    } catch (err) {
      console.error("Upload failed:", err);
      setUploading(false);
    }
  };

  const saveBankDetails = async () => {
    setSavingBank(true);
    try {
      await setDoc(doc(db, "employees", user.email, "bankDetails", "info"), bank, { merge: true });
      setBankSuccess(true);
      setShowBankForm(false);
      setTimeout(() => setBankSuccess(false), 3000);
    } catch (err) {
      alert("Failed to save bank details");
    }
    setSavingBank(false);
  };

  const handleChangePassword = async () => {
    setPassError("");
    setPassSuccess("");

    if (newPass !== confirmPass) return setPassError("Passwords don't match");
    if (newPass.length < 6) return setPassError("Password must be 6+ characters");

    setChanging(true);
    try {
      const employeeRef = doc(db, "employees", user.email);
      const snap = await getDoc(employeeRef);
      if (snap.data().password !== currentPass) {
        setPassError("Current password is wrong");
        setChanging(false);
        return;
      }

      await updateDoc(employeeRef, { password: newPass });
      setPassSuccess("Password updated!");
      setCurrentPass(""); setNewPass(""); setConfirmPass("");
      setTimeout(() => {
        setShowPassForm(false);
        setPassSuccess("");
      }, 2000);
    } catch (err) {
      setPassError("Failed to update password");
    }
    setChanging(false);
  };

  return (
    <div className="h-full bg-gradient-to-br from-neutral-950 via-purple-950 to-indigo-950 flex flex-col">

      {/* Hero */}
      <div className="px-6 pt-6 pb-4 text-center flex-shrink-0">
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-2xl font-black bg-gradient-to-r from-white via-white/90 to-white/70 bg-clip-text text-transparent"
        >
          My Profile
        </motion.h1>
      </div>

      {/* Glass Card */}
      <div className="flex-1 overflow-y-auto px-6 pb-6">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          className="backdrop-blur-3xl bg-white/10 border border-white/20 rounded-3xl p-8 shadow-2xl max-w-4xl mx-auto"
        >
          {/* Avatar + Upload */}
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="relative group">
              <div className="w-32 h-32 rounded-full overflow-hidden ring-4 ring-cyan-400/50 shadow-2xl">
                <img
                  src={employee?.profilePic || `https://ui-avatars.com/api/?name=${encodeURIComponent(employee?.name || user.email)}&background=0f172a&color=06b6d4&bold=true`}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              </div>
              <label className="absolute bottom-1 right-1 bg-gradient-to-r from-cyan-500 to-blue-500 p-3 rounded-full cursor-pointer shadow-xl hover:scale-110 transition-all">
                <FaCamera className="text-white text-lg" />
                <input
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e.target.files?.[0])}
                />
              </label>
              {uploading && (
                <div className="absolute inset-0 bg-black/60 backdrop-blur-sm rounded-full flex items-center justify-center">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    className="w-6 h-6 border-4 border-white border-t-transparent rounded-full"
                  />
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex-1 text-white space-y-2">
              <div>
                <h2 className="text-2xl font-black bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                  {user?.name || "Employee"}
                </h2>
                <p className="text-white/70 text-sm mt-0.5">{user?.designation || "Team Member"}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-4">
                  <p className="text-white/60 text-[10px] uppercase tracking-wider font-semibold">Employee ID</p>
                  <p className="text-lg font-bold text-cyan-300">{user?.employeeId || "—"}</p>
                </div>
                <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-4">
                  <p className="text-white/60 text-[10px] uppercase tracking-wider font-semibold">Department</p>
                  <p className="text-lg font-bold text-purple-300">{user?.department || "—"}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 text-white/80">
                <FaEnvelope className="text-cyan-400 text-sm" />
                <span className="text-xs">{user.email}</span>
              </div>

              <div className="text-xs text-white/60">
                Joined on:{" "}
                <span className="text-cyan-300 font-medium">
                  {employee?.joinDate ? new Date(employee.joinDate).toLocaleDateString() : "—"}
                </span>
              </div>
            </div>
          </div>

          {/* BANK DETAILS */}
          <div className="mt-4 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-5">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-white">Bank Details</h3>
              {!showBankForm && (
                <button
                  onClick={() => setShowBankForm(true)}
                  className="text-cyan-400 hover:text-cyan-300 text-sm transition flex items-center gap-2"
                >
                  <FaEdit /> Edit
                </button>
              )}
            </div>

            {showBankForm ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input placeholder="Account Holder Name" value={bank.name} onChange={(e) => setBank({ ...bank, name: e.target.value })} className="px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 text-sm" />
                  <input placeholder="Bank Name" value={bank.bankName} onChange={(e) => setBank({ ...bank, bankName: e.target.value })} className="px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 text-sm" />
                  <input placeholder="Account Number" value={bank.account} onChange={(e) => setBank({ ...bank, account: e.target.value })} className="px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 text-sm" />
                  <input placeholder="IFSC Code" value={bank.ifsc} onChange={(e) => setBank({ ...bank, ifsc: e.target.value })} className="px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 text-sm" />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={saveBankDetails}
                    disabled={savingBank}
                    className="flex-1 py-2 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl font-bold text-white hover:scale-105 transition disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
                  >
                    {savingBank ? "Saving..." : <><FaCheck /> Save</>}
                  </button>
                  <button
                    onClick={() => setShowBankForm(false)}
                    className="px-6 py-2 bg-white/10 rounded-xl text-white hover:bg-white/20 transition flex items-center gap-2 text-sm"
                  >
                    <FaTimes /> Cancel
                  </button>
                </div>
                {bankSuccess && <p className="text-green-400 text-xs">Bank details saved!</p>}
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-white/80">
                <div>
                  <p className="text-white/60 text-[10px] uppercase tracking-wider">Account Holder</p>
                  <p className="font-medium text-sm truncate">{bank.name || "—"}</p>
                </div>
                <div>
                  <p className="text-white/60 text-[10px] uppercase tracking-wider">Bank</p>
                  <p className="font-medium text-sm truncate">{bank.bankName || "—"}</p>
                </div>
                <div>
                  <p className="text-white/60 text-[10px] uppercase tracking-wider">Account Number</p>
                  <p className="font-medium text-sm truncate">{bank.account ? `•••• ${bank.account.slice(-4)}` : "—"}</p>
                </div>
                <div>
                  <p className="text-white/60 text-[10px] uppercase tracking-wider">IFSC</p>
                  <p className="font-medium text-sm truncate">{bank.ifsc || "—"}</p>
                </div>
              </div>
            )}
          </div>

          {/* CHANGE PASSWORD */}
          <div className="mt-4 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-5">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-white">Change Password</h3>
              {!showPassForm && (
                <button
                  onClick={() => setShowPassForm(true)}
                  className="text-cyan-400 hover:text-cyan-300 text-sm transition flex items-center gap-2"
                >
                  <FaKey /> Change
                </button>
              )}
            </div>

            {showPassForm ? (
              <div className="space-y-4">
                <input
                  type="password"
                  placeholder="Current Password"
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 text-sm"
                  value={currentPass}
                  onChange={(e) => setCurrentPass(e.target.value)}
                />
                <input
                  type="password"
                  placeholder="New Password"
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 text-sm"
                  value={newPass}
                  onChange={(e) => setNewPass(e.target.value)}
                />
                <input
                  type="password"
                  placeholder="Confirm New Password"
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 text-sm"
                  value={confirmPass}
                  onChange={(e) => setConfirmPass(e.target.value)}
                />
                <div className="flex gap-3">
                  <button
                    onClick={handleChangePassword}
                    disabled={changing}
                    className="flex-1 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-xl font-bold text-white hover:scale-105 transition disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
                  >
                    {changing ? "Updating..." : <><FaCheck /> Update</>}
                  </button>
                  <button
                    onClick={() => {
                      setShowPassForm(false);
                      setCurrentPass(""); setNewPass(""); setConfirmPass("");
                      setPassError(""); setPassSuccess("");
                    }}
                    className="px-6 py-2 bg-white/10 rounded-xl text-white hover:bg-white/20 transition flex items-center gap-2 text-sm"
                  >
                    <FaTimes /> Cancel
                  </button>
                </div>
                {passError && <p className="text-red-400 text-xs">{passError}</p>}
                {passSuccess && <p className="text-green-400 text-xs">{passSuccess}</p>}
              </div>
            ) : (
              <p className="text-white/60 text-sm">Click "Change" to update your password</p>
            )}
          </div>

          <button
            onClick={logout}
            className="mt-3 p-4 bg-red-500/20 hover:bg-red-500/40 rounded-2xl text-red-300 transition flex items-center justify-center gap-2 w-full text-sm"
          >
            <FaSignOutAlt /> Logout
          </button>
        </motion.div>

      </div>



      {/* Footer */}
      <div className="px-8 py-3 text-center flex-shrink-0">
        <p className="text-xs text-gray-500">
          Powered by <span className="font-bold text-cyan-300">DriveEasy HR</span>
        </p>
      </div>
    </div>
  );
}